<template>
  <div v-if="!mobileAllowed" class="mobile-block-page">
    <van-nav-bar title="录入印章点位" left-arrow @click-left="handleBack" />
    <div class="mobile-block-body">
      <p class="mobile-block-title">请使用手机访问</p>
      <p class="mobile-block-desc">印章点位录入仅支持移动端，请在手机浏览器或微信中打开。</p>
    </div>
  </div>
  <div v-else class="seal-points-input-page">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      title="录入印章点位"
      left-arrow
      @click-left="handleBack"
    />

    <div class="content-wrapper">
      <!-- 规则选择 -->
      <div class="form-section">
        <van-field
          v-model="selectedRuleRemark"
          label="选择规则"
          placeholder="请选择规则"
          readonly
          is-link
          @click="showRulePicker = true"
        />
        <van-popup v-model="showRulePicker" position="bottom" get-container="body" class="seal-picker-popup">
          <van-picker
            show-toolbar
            title="选择规则"
            :columns="ruleColumns"
            :default-index="ruleDefaultIndex"
            :item-height="44"
            @confirm="onRuleConfirm"
            @cancel="showRulePicker = false"
          />
        </van-popup>
      </div>

      <!-- 盖章区域 -->
      <div class="form-section">
        <van-field
          :value="selectedAreaLabel"
          label="盖章区域"
          placeholder="请选择盖章区域"
          readonly
          is-link
          @click="showAreaPicker = true"
        />
        <van-popup v-model="showAreaPicker" position="bottom" get-container="body" class="seal-picker-popup">
          <van-picker
            :key="'area-picker-' + areaId"
            show-toolbar
            title="选择盖章区域"
            :columns="areaColumns"
            :default-index="areaDefaultIndex"
            :item-height="44"
            @confirm="onAreaConfirm"
            @cancel="showAreaPicker = false"
          />
        </van-popup>
        <p class="area-hint">{{ areaPreset.hint }}（coordinate_system={{ areaPreset.coordinateSystem }}）</p>
      </div>

      <!-- 规则信息显示 -->
      <div v-if="currentRule" class="rule-info">
        <p><strong>规则编码:</strong> {{ currentRule.ruleCode }}</p>
        <p><strong>已录入记录数:</strong> {{ existingRulesList.length }}</p>
      </div>

      <!-- 印章区域 -->
      <div class="seal-section">
        <div class="seal-label">用印章按压屏幕记录点位（4个触点）</div>
        <div
          v-if="!isFullscreenMode"
          class="canvas-container seal-canvas-fullbleed"
          :style="canvasWrapperStyle"
        >
          <canvas
            ref="canvas"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
          ></canvas>
          <p class="hint">在 {{ areaPreset.label }} 区域内按压印章（逻辑 {{ areaPreset.width }}×{{ areaPreset.height }}）</p>
        </div>
        <div v-else class="fullscreen-entry-section">
          <van-button type="primary" block round @click="openFullscreenTouch">
            进入全屏录入
          </van-button>
          <p class="hint fullscreen-hint">全屏模式下坐标归一化为 0–10000，与屏幕尺寸无关</p>
        </div>

        <seal-fullscreen-touch
          v-model="showFullscreenTouch"
          :area-id="areaId"
          title="全屏点位录入"
          label="四指同时按下，松手后记录"
          @confirm="onFullscreenTouchConfirm"
        />
        
        <!-- 点位信息显示 -->
        <div v-if="touchPoints.length > 0" class="points-info">
          <p><strong>当前点位 ({{ touchPoints.length }}/4):</strong></p>
          <p v-for="(point, idx) in touchPoints" :key="idx">
            点{{ idx + 1 }}: [{{ point[0] }}, {{ point[1] }}]（{{ areaPreset.label }}）
          </p>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-section">
        <van-button
          type="warning"
          size="large"
          block
          @click="handleClear"
          :disabled="touchPoints.length === 0"
        >
          清除当前点位
        </van-button>
        <van-button
          type="primary"
          size="large"
          block
          @click="handleAddPoints"
          :disabled="touchPoints.length !== 4 || !currentRule"
          :loading="saving"
          style="margin-top: 12px;"
        >
          新增点位记录
        </van-button>
      </div>

      <!-- 已录入点位列表 -->
      <div v-if="currentRule && existingRulesList.length > 0" class="points-list-section">
        <div class="section-title">已录入的记录 (共{{ existingRulesList.length }}条)</div>
        <div
          v-for="(ruleRecord, idx) in existingRulesList"
          :key="idx"
          class="points-group"
        >
          <div class="group-header">第{{ idx + 1 }}条记录 - 匹配分数: {{ ruleRecord.point }}分</div>
          <div class="group-points">
            <span
              v-for="(point, pIdx) in ruleRecord.points"
              :key="pIdx"
              class="point-item"
            >
              [{{ point[0] }}, {{ point[1] }}]
            </span>
          </div>
        </div>
      </div>

      <!-- 提示信息 -->
      <div class="tip-section">
        <van-notice-bar
          text="提示：每次按压印章后，点击'新增点位记录'按钮，系统会先匹配计算分数，然后新增一条记录。匹配分数会存储到point字段。"
          left-icon="info-o"
          color="#1989fa"
          background="#ecf9ff"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getSealRules, addSealPoints } from '@/api/sealTest'
import { Toast } from 'vant'
import {
  DEFAULT_SEAL_AREA_ID,
  SEAL_AREA_PRESETS,
  getSealAreaPreset,
  mapTouchToStandard,
  buildSealPointsSubmitPayload,
  isSealFullscreenMode
} from '@/pages/utils/seal-area-presets'
import {
  sealMobileOnlyMixin,
  syncSealCanvasSize,
  resolveVanPickerOption,
  formatSealRuleLabel
} from '@/pages/utils/seal-mobile-only'
import SealFullscreenTouch from '@/pages/components/SealFullscreenTouch.vue'

export default {
  name: 'SealPointsInput',
  components: { SealFullscreenTouch },
  mixins: [sealMobileOnlyMixin],
  data() {
    return {
      allRules: [],
      ruleColumns: [],
      showRulePicker: false,
      selectedRuleId: null, // 现在存储的是rule_code
      selectedRuleRemark: '',
      currentRule: null, // {ruleCode: 'xxx', remark: 'xxx'}
      existingRulesList: [], // 已录入的记录列表 [{points: [], point: 85}, ...]
      touchPoints: [],
      canvas: null,
      ctx: null,
      saving: false,
      areaId: DEFAULT_SEAL_AREA_ID,
      areaOptions: SEAL_AREA_PRESETS,
      areaColumns: SEAL_AREA_PRESETS.map((item) => ({ text: item.label, value: item.id })),
      showAreaPicker: false,
      showFullscreenTouch: false
    }
  },
  computed: {
    areaPreset() {
      return getSealAreaPreset(this.areaId)
    },
    selectedAreaLabel() {
      return this.areaPreset.label
    },
    isFullscreenMode() {
      return isSealFullscreenMode(this.areaId)
    },
    canvasWrapperStyle() {
      const ar = this.areaPreset.aspectRatio
      return ar ? { '--seal-aspect-ratio': ar } : {}
    },
    areaDefaultIndex() {
      const idx = this.areaOptions.findIndex((item) => item.id === this.areaId)
      return idx >= 0 ? idx : 0
    },
    ruleDefaultIndex() {
      if (!this.selectedRuleId) return 0
      const idx = this.ruleColumns.findIndex((item) => item.value === this.selectedRuleId)
      return idx >= 0 ? idx : 0
    }
  },
  mounted() {
    if (!this.mobileAllowed) return
    this.initCanvas()
    this.loadRules()
  },
  methods: {
    initCanvas() {
      this.$nextTick(() => {
        this.canvas = this.$refs.canvas
        this.syncCanvasSize()
        window.setTimeout(() => this.syncCanvasSize(), 80)
      })
    },
    syncCanvasSize() {
      syncSealCanvasSize(this)
    },
    onAreaChange() {
      this.showFullscreenTouch = false
      this.handleClear()
      if (!this.isFullscreenMode) {
        this.initCanvas()
      }
    },
    openFullscreenTouch() {
      if (!this.currentRule) {
        Toast.fail('请先选择规则')
        return
      }
      this.showFullscreenTouch = true
    },
    onFullscreenTouchConfirm(points) {
      this.showFullscreenTouch = false
      this.touchPoints = points
    },
    onAreaConfirm(payload) {
      const nextId = resolveVanPickerOption(payload)
      if (nextId && nextId !== this.areaId) {
        this.areaId = nextId
        this.onAreaChange()
      }
      this.showAreaPicker = false
    },
    async loadRules() {
      try {
        const res = await getSealRules()
        const rules = res.data || res
        // rules现在是从SQL返回的Map列表，包含rule_code和remark
        this.allRules = rules
        this.ruleColumns = rules.map((rule) => ({
          text: formatSealRuleLabel(rule),
          value: rule.ruleCode,
          ruleCode: rule.ruleCode,
          remark: rule.remark
        }))
      } catch (err) {
        console.error('加载规则失败:', err)
        Toast.fail('加载规则失败')
      }
    },
    onRuleConfirm(payload) {
      const ruleCode = resolveVanPickerOption(payload)
      const rule = this.allRules.find((r) => r.ruleCode === ruleCode)
      if (!rule || !ruleCode) {
        Toast.fail('请选择有效规则')
        return
      }
      this.selectedRuleId = ruleCode
      this.selectedRuleRemark = formatSealRuleLabel(rule)
      this.currentRule = {
        ruleCode: rule.ruleCode,
        remark: rule.remark
      }
      this.loadExistingRules(rule.ruleCode)
      this.showRulePicker = false
    },
    async loadExistingRules(ruleCode) {
      this.existingRulesList = []
      
      try {
        const res = await getSealRules()
        const allRules = res.data || res
        // 筛选出相同ruleCode的所有记录
        const rules = allRules.filter(r => r.ruleCode === ruleCode && r.id !== this.selectedRuleId)
        
        // 解析每条记录的点位
        for (const rule of rules) {
          if (rule.sealPoints) {
            try {
              const points = JSON.parse(rule.sealPoints)
              if (Array.isArray(points) && points.length === 4) {
                this.existingRulesList.push({
                  id: rule.id,
                  points: points,
                  point: rule.point || 0,
                  remark: rule.remark
                })
              }
            } catch (e) {
              console.error('解析点位失败:', e, rule)
            }
          }
        }
      } catch (e) {
        console.error('加载已录入记录失败:', e)
      }
    },
    handleTouchStart(e) {
      e.preventDefault()
      if (!this.canvas || !this.ctx || e.touches.length === 0) return
      this.syncCanvasSize()
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.touchPoints = []
        
        const rect = this.canvas.getBoundingClientRect()
        const scaleX = this.canvas.width / rect.width
        const scaleY = this.canvas.height / rect.height

        for (let i = 0; i < e.touches.length; i++) {
          const touch = e.touches[i]
          const mapped = mapTouchToStandard(touch, rect, this.areaId)
          const canvasX = mapped.displayX * scaleX
          const canvasY = mapped.displayY * scaleY

          this.touchPoints.push([mapped.standardX, mapped.standardY])
          this.drawPoint(canvasX, canvasY, i + 1)
        }

        console.log(`${e.touches.length}个触点，区域 ${this.areaPreset.label}，点位记录:`, this.touchPoints)
    },
    handleTouchEnd(e) {
      // 手指离开时不做处理
    },
    drawPoint(x, y, index) {
      // 绘制红色圆点
      this.ctx.fillStyle = '#FF0000'
      this.ctx.beginPath()
      this.ctx.arc(x, y, 8, 0, 2 * Math.PI)
      this.ctx.fill()
      
      // 绘制序号文字
      this.ctx.fillStyle = '#FFFFFF'
      this.ctx.font = 'bold 12px Arial'
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'middle'
      this.ctx.fillText(index.toString(), x, y)
    },
    handleClear() {
      if (this.canvas && this.ctx) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      }
      this.touchPoints = []
    },
    async handleAddPoints() {
      if (!this.currentRule) {
        Toast.fail('请先选择规则')
        return
      }

      if (this.touchPoints.length !== 4) {
        Toast.fail('必须记录4个点位')
        return
      }

      this.saving = true
      try {
        const submitPayload = buildSealPointsSubmitPayload(this.touchPoints, this.areaId)
        await addSealPoints({
          ruleCode: this.currentRule.ruleCode,
          sealPoints: submitPayload.points,
          coordinateSystem: submitPayload.coordinateSystem,
          remark: this.currentRule.remark || this.currentRule.ruleCode || ''
        })

        Toast.success('点位记录新增成功！')
        
        // 重新加载规则列表和已录入记录
        await this.loadRules()
        if (this.currentRule && this.currentRule.ruleCode) {
          await this.loadExistingRules(this.currentRule.ruleCode)
        }
        
        // 清空当前点位
        this.handleClear()
      } catch (error) {
        console.error('追加点位失败:', error)
        Toast.fail(error.message || '追加点位失败，请重试')
      } finally {
        this.saving = false
      }
    },
    handleBack() {
      this.$router.push('/sealNav')
    }
  }
}
</script>

<style scoped lang="less">
.mobile-block-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.mobile-block-body {
  padding: 48px 24px;
  text-align: center;
}

.mobile-block-title {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.mobile-block-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #666;
}

.seal-points-input-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.content-wrapper {
  padding: 12px;
}

.form-section {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.form-section ::v-deep .van-field__control {
  line-height: 24px;
  min-height: 24px;
}

.area-hint {
  margin: 0;
  padding: 0 16px 12px;
  font-size: 12px;
  color: #999;
}

.rule-info {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #333;
}

.rule-info p {
  margin: 4px 0;
}

.seal-section {
  background: #fff;
  border-radius: 8px;
  padding: 12px 0;
  margin-bottom: 12px;
  overflow: hidden;
}

.seal-label,
.hint,
.points-info {
  margin-left: 12px;
  margin-right: 12px;
}

.seal-canvas-fullbleed {
  width: 100vw;
  margin: 12px 0 0;
  margin-left: calc(50% - 50vw);
  padding: 0;
  background: #fff;
}

.seal-canvas-fullbleed canvas {
  display: block;
  width: 100%;
  height: auto;
  max-width: none;
  border: none;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  border-radius: 0;
  touch-action: none;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  aspect-ratio: var(--seal-aspect-ratio, 800 / 600);
}

.fullscreen-entry-section {
  margin: 0 12px 12px;
}

.fullscreen-hint {
  margin-top: 12px;
  text-align: center;
}

.seal-label {
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
  font-weight: 500;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 8px;
}

.points-info {
  background: white;
  padding: 12px;
  margin: 12px 0;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.points-info p {
  margin: 4px 0;
}

.action-section {
  margin-bottom: 12px;
}

.points-list-section {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 12px;
}

.points-group {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
}

.group-header {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.group-points {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.point-item {
  font-size: 12px;
  color: #333;
  background: #fff;
  padding: 2px 6px;
  border-radius: 2px;
}

.tip-section {
  margin-top: 12px;
}
</style>

<style lang="less">
.seal-picker-popup .van-picker-column__item {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.3;
  padding: 0 12px;
  box-sizing: border-box;
}
</style>

